import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Widget from '../../models/widget';
import WidgetEvent from '../../models/widgetEvent';
import { Icon, Table, DropdownItem, DropdownSort, EmptyState, Card, Message } from '@weezevent/nacre';
import WidgetRow from './widgetRow';
import SearchModule from './searchModule';
import Style from './widgetRow.module.css';
import { useFilters, handleSearchQuery } from '../manager/components/context';
import LoadingTable from '../../components/loadingTable';

const spec = {
    multiple: ['module_type'],
    fields: ['search', 'event__in', 'module_type'],
    aliases: {},
    date: []
};

export default function WidgetList({ organizationId, usages, withWidgetEvents, context, onlyWidgetEngine = false }) {
    const { t } = useTranslation();
    const [filters, setFilters] = useFilters(spec);
    const [widgets, loadingWidgets, syncWidget] = Widget.useApiModel(
        {
            organizationId,
            query: {
                usage__in: usages,
                ...handleSearchQuery(spec, filters, true),
                ...(onlyWidgetEngine && { only_widget_engine: true })
            }
        },
        [filters, spec, organizationId, usages, onlyWidgetEngine]
    );

    const [widgetEvents] = WidgetEvent.useApiModel(
        {
            organizationId,
            launch: withWidgetEvents,
            query: {
                include_finished: 'False',
                expand: 'event',
                ...handleSearchQuery(spec, filters, true)
            }
        },
        [filters, spec, organizationId, withWidgetEvents]
    );

    let allWidgets = React.useMemo(() => {
        const _widgetsEvents = widgetEvents || [];
        const _widgets = widgets || [];
        const moduleTypeArray = filters['module_type'] ? Array.from(filters['module_type']) : [];
        if (moduleTypeArray.length === 1 && moduleTypeArray[0] === 'filtered') {
            return _widgetsEvents;
        }
        if (moduleTypeArray.length === 1 && moduleTypeArray[0] === 'multi-event') {
            return _widgets;
        }

        return [..._widgets, ..._widgetsEvents];
    }, [widgets, widgetEvents]);

    const [sortType, setSortType] = React.useState('-asc');

    const sortedWidget = React.useMemo(() => {
        if (!allWidgets) {
            return [];
        }
        switch (sortType) {
            case 'asc':
                allWidgets.sort((a, b) => a.created.localeCompare(b.created));
                break;
            case '-asc':
                allWidgets.sort((b, a) => a.created.localeCompare(b.created));
                break;
            case 'alpha':
                allWidgets.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case '-alpha':
                allWidgets.sort((b, a) => a.name.localeCompare(b.name));
                break;
            default:
                break;
        }

        return allWidgets;
    }, [allWidgets, sortType]);

    const [selectAll, setSelectAll] = React.useState(false);

    return (
        <React.Fragment>
            {widgetEvents?.length > 0 && (
                <Message
                    title={t('ticket.widget.bannerOldBo.title')}
                    type="warning"
                    links={{
                        label: t('ticket.widget.bannerOldBo.cta'),
                        href: `${window.weezConfig.temporary.menhirUrl}/bo/multi_billetterie.php`
                    }}
                >
                    {t('ticket.widget.bannerOldBo.content')}
                </Message>
            )}
            <Card>
                <SearchModule
                    organizationId={organizationId}
                    sortedWidget={sortedWidget}
                    filters={filters}
                    setFilters={setFilters}
                    loadingWidgets={loadingWidgets}
                    context={context}
                />
                <div>
                    <Table
                        useNew
                        selectable
                        onSelectAll={() => {
                            setSelectAll(!selectAll);
                        }}
                        className={Style['list__table']}
                    >
                        <Table.THead>
                            <Table.Tr active={selectAll}>
                                <th>
                                    <Table.DropdownSort
                                        left
                                        label={t('ticket.widget.table_header.module_name')}
                                        name="alpha"
                                        active={['alpha', `-alpha`].includes(sortType)}
                                        ordering={sortType}
                                        size="mini"
                                        items={[
                                            <DropdownItem.Icons
                                                key={1}
                                                checked={sortType === 'alpha'}
                                                item={t('fromRateCategory', { min: 'A', max: 'Z' })}
                                                onClick={() => {
                                                    setSortType('alpha');
                                                }}
                                            />,
                                            <DropdownItem.Icons
                                                key={2}
                                                checked={sortType === '-alpha'}
                                                item={t('fromRateCategory', { min: 'Z', max: 'A' })}
                                                onClick={() => {
                                                    setSortType('-alpha');
                                                }}
                                            />
                                        ]}
                                    />
                                </th>
                                <th>
                                    <DropdownSort
                                        left
                                        label={t('ticket.widget.table_header.creation_date')}
                                        name="asc"
                                        active={['asc', `-asc`].includes(sortType)}
                                        ordering={sortType}
                                        size="mini"
                                        items={[
                                            <DropdownItem.Icons
                                                key={1}
                                                checked={sortType === 'asc'}
                                                item={t('ticket.accounting.filter.increasing')}
                                                onClick={() => {
                                                    setSortType('asc');
                                                }}
                                            />,
                                            <DropdownItem.Icons
                                                key={2}
                                                checked={sortType === '-asc'}
                                                item={t('ticket.accounting.filter.descending')}
                                                onClick={() => {
                                                    setSortType('-asc');
                                                }}
                                            />
                                        ]}
                                    />
                                </th>
                                {/* <th>{t('ticket.widget.table_header.module_type')}</th> */} {/* TODO: uncomment when module type will be functional */}
                                <th>{t('ticket.widget.table_header.events')}</th>
                                <th style={{ textAlign: 'left' }} className={Style['width-regular']}>
                                    {t('ticket.widget.table_header.actions')}
                                </th>
                            </Table.Tr>
                        </Table.THead>
                        {sortedWidget?.length > 0 && (
                            <Table.TBody>
                                {sortedWidget.map((sortwidget, index) => {
                                    return (
                                        <WidgetRow
                                            key={index}
                                            widget={sortwidget}
                                            organizationId={organizationId}
                                            selectAll={selectAll}
                                            triggerRefetch={syncWidget}
                                            context={context}
                                        />
                                    );
                                })}
                            </Table.TBody>
                        )}
                    </Table>
                    <LoadingTable loading={loadingWidgets} />
                    {!loadingWidgets && sortedWidget?.length === 0 && (
                        <EmptyState icon={<Icon name="list" color="black" />} title={t('ticket.widget.empty_state.title')} subtitle={t('ticket.widget.empty_state.subtitle')} />
                    )}
                </div>
            </Card>
        </React.Fragment>
    );
}
WidgetList.defaultProps = {
    usages: ['sale', 'resale', 'manage_order', 'multi_events', 'personal_space'],
    withWidgetEvents: false,
    context: 'default'
};

WidgetList.propTypes = {
    organizationId: PropTypes.string.isRequired,
    usages: PropTypes.array,
    withWidgetEvents: PropTypes.bool,
    context: PropTypes.string
};
